const {LitElement, html, css, ref, createRef} = window.globalLit;

import { linearConversion } from '../functions.js';

const STATUS = Object.freeze({
    PAUSED   : 'PAUSED',
    RECORDING: 'RECORDING',
    REPLAYING: 'REPLAYING',
});

export class WebsocketCanvasElement extends LitElement {

    static properties = {
        startUrl:   { type: String, attribute: 'start-url' },
        status:     { type: String },
        socketUrl:  { type: String, attribute: 'socket-url' },
        currentUrl: { type: String },
        width:      { type: Number, attribute: 'width' },
        height:     { type: Number, attribute: 'height' },
    };

    createRenderRoot() { return this; }

    constructor() {
        super();
        this.startUrl = null;
        this.socketUrl = null;
        this.currentUrl = '';
        this.status = this.status || STATUS.RECORDING;
        this.canvas = null;
        this.width = 1920;
        this.height = 1080;
        this.conn = null;
        this.lastEventSent = null;
        this.keydownString = '';
        this.keydownTimer = null;
    }

    toggleStatus() {
        this.status = this.status === STATUS.PAUSED ? STATUS.RECORDING : STATUS.PAUSED;
        // You may want to add additional logic here, such as notifying the server about the status change
    }

    firstUpdated() {
        if (!this.startUrl) {
            alert('start-url attribute is required');
            return;
        }

        this.canvas = this.querySelector('.browser__canvas');

        this.setCanvasSize();

        this.createWebsocket();

    }

    setCanvasSize() {
        let aspectRatio = this.width / this.height;

        this.canvas.style.aspectRatio = aspectRatio;

        this.canvas.width = this.width;
        this.canvas.style.width = this.width + 'px';

        this.canvas.height = this.height;

        this.style.width = this.width + 'px';
    }

    createWebsocket() {
        if (!this.socketUrl) {
            console.error('socket-url attribute is required');
            return;
        }

        this.conn = new WebSocket(this.socketUrl);
        this.conn.onopen    = this.onOpen.bind(this);
        this.conn.onmessage = this.onMessage.bind(this);
        this.conn.onclose   = this.onClose.bind(this);
        this.conn.onerror   = this.onError.bind(this);
    }

    onCanvasMouseClick(event) {
        var rect = event.target.getBoundingClientRect();
        var x = event.clientX - rect.left; 
        var y = event.clientY - rect.top;
        
        let position = this.convertToCanvasDimensions(x, y);

        this.sendClickEvent(position);
    }

    onCanvasKeydown(event) {
        
        if (event.key.length !== 1) { // Evitamos Shift, Enter, Ctrl, Dead, etc
            return;
        }

        this.conn.send(JSON.stringify({
            type: 'keydown',
            key: event.key,
        }));

        this.keydownString += event.key;

        clearTimeout(this.keydownTimer);

        this.keydownTimer = setTimeout(() => {

            if (this.status === STATUS.RECORDING) {
                let customEvent = new CustomEvent('canvas.action', {
                    detail: {
                        type: 'type',
                        text: this.keydownString
                    },
                    bubbles: true,
                });
                this.dispatchEvent(customEvent);
            }

            this.keydownString = '';

        }, 1000);

        
    }

    onCanvasWheel(event) {
        event.preventDefault();

        let pageCoords = this.convertToCanvasDimensions(event.layerX, event.layerY);

        this.conn.send(JSON.stringify({
            type: 'wheel',
            deltaX: event.deltaX,
            deltaY: event.deltaY,
            deltaZ: event.deltaZ,
            deltaMode: event.deltaMode,
            pageX: pageCoords.x,
            pageY: pageCoords.y,
        }));
    }

    sendClickEvent(position) {
        this.conn.send(JSON.stringify({
            type: 'click',
            position: position
        }))
        this.lastEventSent = 'click';
    }

    convertToCanvasDimensions(x, y) {
        if (this.width && this.height) {
            let rect = this.canvas.getBoundingClientRect();
            return {
                x: linearConversion(x, 0, rect.width, 0, this.width),
                y: linearConversion(y, 0, rect.height, 0, this.height),
            }
        } else {
            return {
                x: x,
                y: y
            };
        }
    }

    onOpen(event) {
        console.log('Connection established!');
    }

    onMessage(event) {
        let data = JSON.parse(event.data);

        if (!data) {
            console.error('Wrong data', event.data);
            return;
        }

        if (data.type === 'init') {
            console.log('Initialization received', data);

            this.conn.send(JSON.stringify({
                type:       'start',
                url:        this.startUrl,
                width: this.width,
                height: this.height,
            }));
            
        }
        else if (data.type === 'screenshot') {
            let ctx = this.canvas.getContext('2d');
            let img = new Image();
            img.onload = function() {
                ctx.drawImage(img, 0, 0);
            }
            img.src = 'data:image/jpeg;base64,' + data.imageData;
        } 
        else if (data.type === 'pageClick') {
            if (this.status !== STATUS.RECORDING) {
                return;
            }
            let customEvent = new CustomEvent('canvas.action', {
                detail: {
                    type: 'click',
                    locator: data.locator
                },
                bubbles: true,
            });
            this.dispatchEvent(customEvent);
        }
        else if (data.type === 'afterStepPlay') {
            let customEvent = new CustomEvent('canvas.afterStepPlay', {
                detail: {
                    step: data.step
                },
                bubbles: true,
            });
            this.dispatchEvent(customEvent);
        }
        else if (data.type === 'afterPlay') {
            this.status = STATUS.RECORDING;
            let customEvent = new CustomEvent('canvas.afterPlay', { bubbles: true, });
            this.dispatchEvent(customEvent);
        }
        else if (data.type === 'stepError') {
            let customEvent = new CustomEvent('canvas.stepError', {
                detail: {
                    step: data.step,
                    error: data.error
                },
                bubbles: true,
            });
            this.dispatchEvent(customEvent);
        }
        else if (data.type === 'urlChange') {
            console.log('URL changed:', data.url);
            this.currentUrl = data.url; // Update the currentUrl property
            // this.requestUpdate(); // Request a re-render to update the URL bar
        }
        else {
            console.log(data);
        }
    }

    onClose(event) {
        console.log('Connection closed!', event);
    }

    onError(event) {
        console.log('Connection error!', event);
    }

    onUrlChange(event) {
        this.currentUrl = event.target.value;
        this.conn.send(JSON.stringify({
            type: 'navigateTo',
            url: this.currentUrl
        }));

        let customEvent = new CustomEvent('canvas.action', {
            detail: {
                type: 'goto',
                url: this.currentUrl
            },
            bubbles: true,
        });
        this.dispatchEvent(customEvent);

    }

    playSteps(steps) {
        this.status = STATUS.REPLAYING;
        this.conn.send(JSON.stringify({
            type: 'playSteps',
            steps: steps
        }));
    }

    render() {

        return html`
            <div class="browser">
                <div class="browser__bar">
                    <div class="browser__url-bar">
                        <input 
                            class="browser__url" 
                            .value=${this.currentUrl || this.startUrl} 
                            @change=${this.onUrlChange}
                        />
                        ${this.status ? html`
                            <span class="browser__status browser__status--${this.status.toLowerCase()}" @click=${this.toggleStatus}>
                                ${(() => {
                                    switch (this.status) {
                                        case STATUS.PAUSED:
                                            return 'NOT CAPTURING';
                                        case STATUS.RECORDING:
                                            return 'CAPTURING EVENTS';
                                        case STATUS.REPLAYING:
                                            return 'REPLAYING';
                                        default:
                                            return 'UNKNOWN';
                                    }
                                })()}
                            </span>
                        ` : ''}
                    </div>
                </div>
                <canvas
                    class="browser__canvas"
                    @click=${this.onCanvasMouseClick}
                    @wheel=${this.onCanvasWheel}
                    @keydown=${this.onCanvasKeydown}
                    tabindex="1"
                ></canvas>
            </div>
        `;
    }
};

customElements.define('websocket-canvas', WebsocketCanvasElement);